/**
 * Die Karte „Server“ — unter „Was gerade läuft“ in den Einstellungen. (18.10.2026, Server-Netz Stufe 1b)
 *
 * Eine Zeile je Server (S6, S7, S8): Name, Ampel als festes Wort UND Zeichen (nie nur Farbe), „zuletzt vor …“ und die Zahlen
 * Platte frei, Speicher frei, Last, offene Updates, Alter der Sicherung. Die Daten kommen von GET /api/system/server-karte
 * (server/server-karte-routen.ts).
 *
 * ⚠️ Nur Konto 1 (shared/entwickler-im-chat.ts); jedes andere Konto bekommt GAR KEIN Element und macht keinen Abruf.
 * ⚠️ Die Karte ist unsichtbar (`data-zeigbar="nein"`, per CSS; `hidden` gehört allein der Suche, die nur verstecken kann), solange der Server nicht ausdrücklich Daten liefert: 401/403 (kein Betreiber) und
 *    409 (Server-Netz aus) lassen sie weg und beenden den Takt — kein Abruf ins Leere. Nur bei Ausfall des Abrufs (Netz, 5xx,
 *    kaputte Antwort) wird sie sichtbar und sagt es ehrlich.
 * ⚠️ Alles, was der Server schickt, sind Daten: gelesen wird streng (Kennung aus der festen Liste, Zahlen im Bereich, Gründe nur
 *    aus der festen Wortliste), angezeigt nur über textContent — nie als HTML, nie ein roher Text aus einer Meldung. Der Name
 *    kommt aus der festen Tabelle, nicht aus der Antwort.
 * ⚠️ Fehlt eine Quelle: „unbekannt“ — nie ein erfundenes Grün. „Grün“ gilt nur, wenn die Meldung da ist und die gemeinsame
 *    Ampelrechnung (shared/server-netz.ts) es bestätigt.
 * ⚠️ Takt: ein Abruf beim Bauen, danach alle 60 s, nur solange die Karte im Dokument hängt, der Tab sichtbar UND die Ansicht
 *    „Einstellungen“ die aktive ist. Dazu ein Knopf „Aktualisieren“.
 */
import { entwicklerImChatFuerKonto } from "../../../shared/entwickler-im-chat.js";
import {
  GRUND_WORTE,
  SERVER_KENNUNGEN,
  SERVER_NAMEN,
  berechneAmpel,
  type Ampel,
  type AmpelGrund,
  type Meldung,
  type ServerKennung,
} from "../../../shared/server-netz.js";

export const SERVER_ABRUF_MS = 60_000;
export const SERVER_MINDEST_MS = 10_000;

/** Zeichen UND Wort je Ampel — verschieden, damit die Farbe es nie allein trägt. */
export const AMPEL_ZEICHEN: Readonly<Record<Ampel, string>> = { gruen: "✓", gelb: "▲", rot: "✕", unbekannt: "?" };
export const AMPEL_WORTE: Readonly<Record<Ampel, string>> = { gruen: "Grün", gelb: "Gelb", rot: "Rot", unbekannt: "Unbekannt" };

/** Was die Oberfläche je Server weiß — streng gelesen. */
export interface ServerAnzeige {
  kennung: ServerKennung;
  name: string;
  ampel: Ampel;
  gruende: AmpelGrund[];
  zuletztVorS: number | null;
  /** Nur die Zahlen, die angezeigt werden. */
  plattenFreiProzent: number | null;
  speicherFreiProzent: number | null;
  last: number | null;
  updatesOffen: number | null;
  updatesSicherheit: number | null;
  sicherungAlterH: number | null;
}

export type ServerAbruf = { art: "ok"; server: ServerAnzeige[] } | { art: "weg" } | { art: "ausfall" };

const AMPELN: readonly Ampel[] = ["gruen", "gelb", "rot", "unbekannt"];
const ZEHN_JAHRE_S = 10 * 366 * 86_400;

function zahl(v: unknown, max: number): number | null {
  return typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= max ? v : null;
}

function leer(kennung: ServerKennung): ServerAnzeige {
  return {
    kennung,
    name: SERVER_NAMEN[kennung],
    ampel: "unbekannt",
    gruende: [],
    zuletztVorS: null,
    plattenFreiProzent: null,
    speicherFreiProzent: null,
    last: null,
    updatesOffen: null,
    updatesSicherheit: null,
    sicherungAlterH: null,
  };
}

/** Die Felder, die die Ampelrechnung braucht, streng gelesen. */
function meldungFuerAmpel(m: Record<string, unknown>, kennung: ServerKennung, frei: number[]): Meldung {
  return {
    kennung,
    zeit: m.zeit as number,
    laufzeitS: null,
    last: zahl(m.last, 100_000),
    platten: frei.map((f) => ({ punkt: "/", freiProzent: f })),
    speicherFreiProzent: zahl(m.speicherFreiProzent, 100),
    updatesOffen: zahl(m.updatesOffen, 100_000),
    updatesSicherheit: zahl(m.updatesSicherheit, 100_000),
    neustartNoetig: typeof m.neustartNoetig === "boolean" ? m.neustartNoetig : null,
    sicherungAlterH: zahl(m.sicherungAlterH, 10 * 366 * 24),
    boteVersion: "x",
  };
}

/** Eine Zeile streng lesen; unlesbar → `null` (dann steht der Server als „unbekannt“ da). Rein. */
function zeileAus(roh: unknown, ts: number): ServerAnzeige | null {
  if (!roh || typeof roh !== "object") return null;
  const r = roh as Record<string, unknown>;
  const kennung = SERVER_KENNUNGEN.find((k) => k === r.kennung);
  if (!kennung) return null;
  const m = r.meldung && typeof r.meldung === "object" ? (r.meldung as Record<string, unknown>) : null;
  const platten = m && Array.isArray(m.platten) ? m.platten : [];
  const frei = platten
    .map((p) => (p && typeof p === "object" ? zahl((p as Record<string, unknown>).freiProzent, 100) : null))
    .filter((x): x is number => x !== null);
  const anzeige: ServerAnzeige = {
    ...leer(kennung),
    ampel: AMPELN.find((a) => a === r.ampel) ?? "unbekannt",
    gruende: Array.isArray(r.gruende)
      ? r.gruende.filter((g): g is AmpelGrund => typeof g === "string" && Object.prototype.hasOwnProperty.call(GRUND_WORTE, g)).slice(0, 8)
      : [],
    // Selbst gerechnet (Antwortzeit minus Messzeit), nicht dem Wert des Servers vertraut.
    zuletztVorS: m && typeof m.zeit === "number" && Number.isFinite(m.zeit) ? zahl(Math.floor(Math.max(0, ts - m.zeit) / 1000), ZEHN_JAHRE_S) : null,
    plattenFreiProzent: frei.length ? Math.min(...frei) : null,
    speicherFreiProzent: m ? zahl(m.speicherFreiProzent, 100) : null,
    last: m ? zahl(m.last, 100_000) : null,
    updatesOffen: m ? zahl(m.updatesOffen, 100_000) : null,
    updatesSicherheit: m ? zahl(m.updatesSicherheit, 100_000) : null,
    sicherungAlterH: m ? zahl(m.sicherungAlterH, 10 * 366 * 24) : null,
  };
  // Nie ein erfundenes Grün: ohne Meldung oder ohne Bestätigung der gemeinsamen Rechnung steht „unbekannt“.
  if (anzeige.ampel === "gruen") {
    const bestaetigt = m && typeof m.zeit === "number" && Number.isFinite(m.zeit) && berechneAmpel(meldungFuerAmpel(m, kennung, frei), ts).ampel === "gruen";
    if (!bestaetigt) return { ...anzeige, ampel: "unbekannt", gruende: [] };
  }
  return anzeige;
}

/** Die Antwort lesen: immer alle bekannten Server in fester Reihenfolge; ein fehlender steht als „unbekannt“ da. Rein. */
export function serverAbrufAus(roh: unknown): ServerAbruf {
  if (!roh || typeof roh !== "object") return { art: "ausfall" };
  const r = roh as Record<string, unknown>;
  if (r.ok !== true || !Array.isArray(r.server)) return { art: "ausfall" };
  const ts = typeof r.ts === "number" && Number.isFinite(r.ts) ? r.ts : Date.now();
  const gelesen = r.server.map((z) => zeileAus(z, ts)).filter((z): z is ServerAnzeige => z !== null);
  return { art: "ok", server: SERVER_KENNUNGEN.map((k) => gelesen.find((z) => z.kennung === k) ?? leer(k)) };
}

/** „zuletzt vor 3 Min.“ — grob und ruhig. Rein. */
export function zuletztText(s: number | null): string {
  if (s === null) return "noch keine Meldung";
  if (s < 60) return "zuletzt gerade eben";
  const min = Math.floor(s / 60);
  if (min < 60) return `zuletzt vor ${min} Min.`;
  const std = Math.floor(min / 60);
  if (std < 48) return `zuletzt vor ${std} Std.`;
  return `zuletzt vor ${Math.floor(std / 24)} Tagen`;
}

const dezimal = (v: number): string => String(Math.round(v * 10) / 10).replace(".", ",");
const prozent = (v: number | null): string => (v === null ? "unbekannt" : `${dezimal(v)} % frei`);

function stundenText(h: number | null): string {
  if (h === null) return "unbekannt";
  if (h < 1) return "vor weniger als 1 Std.";
  if (h < 48) return `vor ${Math.floor(h)} Std.`;
  return `vor ${Math.floor(h / 24)} Tagen`;
}

function updatesText(offen: number | null, sicherheit: number | null): string {
  if (offen === null) return "unbekannt";
  if (offen === 0) return "keine offen";
  return sicherheit ? `${offen} offen, davon ${sicherheit} Sicherheit` : `${offen} offen`;
}

/** Die Zahlen einer Zeile als (Name, Wort)-Paare in fester Reihenfolge. Rein. */
export function serverWerte(z: ServerAnzeige): [string, string][] {
  return [
    ["Platte", prozent(z.plattenFreiProzent)],
    ["Speicher", prozent(z.speicherFreiProzent)],
    ["Last", z.last === null ? "unbekannt" : dezimal(z.last)],
    ["Updates", updatesText(z.updatesOffen, z.updatesSicherheit)],
    ["Sicherung", stundenText(z.sicherungAlterH)],
  ];
}

export interface ServerKarteKontext {
  uid: number | null | undefined;
  /** Für Tests ersetzbar: den Stand holen. */
  hole?: () => Promise<ServerAbruf>;
  /** Für Tests ersetzbar: zeigt der Nutzer gerade die Einstellungen? */
  sichtbar?: () => boolean;
  /** Für Tests ersetzbar: der Takt (Standard 60 s). */
  takt?: number;
  /** Für Tests ersetzbar: Mindestabstand zweier selbsttätiger Abrufe (Standard 10 s; der Knopf zählt nicht). */
  mindestMs?: number;
}

function einstellungenOffen(): boolean {
  return document.body.dataset.activeView === "settings";
}

async function abrufVomServer(): Promise<ServerAbruf> {
  try {
    const r = await fetch("/api/system/server-karte", { credentials: "same-origin", cache: "no-store" });
    if (r.status === 401 || r.status === 403 || r.status === 409) return { art: "weg" };
    return r.ok ? serverAbrufAus(await r.json()) : { art: "ausfall" };
  } catch {
    return { art: "ausfall" };
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, klasse: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

function zeileDom(z: ServerAnzeige): HTMLElement {
  const li = el("li", "einst-server-zeile");
  li.dataset.kennung = z.kennung;
  li.dataset.ampel = z.ampel;
  const kopf = el("div", "einst-server-kopf");
  const ampel = el("span", "einst-server-ampel");
  ampel.dataset.ampel = z.ampel;
  const zeichen = el("span", "einst-server-zeichen", AMPEL_ZEICHEN[z.ampel]);
  zeichen.setAttribute("aria-hidden", "true");
  ampel.append(zeichen, el("span", "einst-server-ampelwort", AMPEL_WORTE[z.ampel]));
  kopf.append(el("span", "einst-server-name", z.name), ampel, el("span", "einst-server-zuletzt", zuletztText(z.zuletztVorS)));
  li.appendChild(kopf);

  const werte = el("dl", "einst-server-werte");
  for (const [name, wert] of serverWerte(z)) {
    const feld = el("div", "einst-server-wert");
    feld.append(el("dt", "einst-server-wert-name", name), el("dd", "einst-server-wert-text", wert));
    werte.appendChild(feld);
  }
  li.appendChild(werte);

  if (z.gruende.length) {
    const gruende = el("ul", "einst-server-gruende");
    for (const g of z.gruende) gruende.appendChild(el("li", "einst-server-grund", GRUND_WORTE[g]));
    li.appendChild(gruende);
  }
  return li;
}

/**
 * Die Karte. Nur Konto 1: sonst `null` (kein Element, kein Abruf). Beginnt unsichtbar und erscheint erst, wenn der Server
 * Daten liefert (oder der Abruf ausfällt, solange das Netz an sein könnte).
 */
export function baueServerKarte(k: ServerKarteKontext): HTMLElement | null {
  if (!entwicklerImChatFuerKonto(k.uid)) return null;
  const hole = k.hole ?? abrufVomServer;
  const sichtbar = k.sichtbar ?? einstellungenOffen;

  const abschnitt = el("section", "einst-gerade einst-server");
  abschnitt.setAttribute("aria-labelledby", "einst-server-titel");
  abschnitt.dataset.zeigbar = "nein";

  const kopf = el("div", "einst-server-titelzeile");
  const titel = el("h3", "einst-gruppe-titel", "Server");
  titel.id = "einst-server-titel";
  const knopf = el("button", "einst-server-knopf", "Aktualisieren");
  knopf.type = "button";
  kopf.append(titel, knopf);

  const liste = el("ul", "einst-gerade-karte einst-server-liste");
  liste.setAttribute("aria-live", "off"); // leise: der Takt soll nichts vorlesen
  const hinweis = el("p", "einst-server-hinweis");
  hinweis.setAttribute("role", "status");
  abschnitt.append(kopf, hinweis, liste);

  const zeige = (a: ServerAbruf | null): void => {
    if (a !== null && a.art === "ok") {
      hinweis.textContent = "";
      hinweis.hidden = true;
      liste.replaceChildren(...a.server.map(zeileDom));
      return;
    }
    hinweis.hidden = false;
    hinweis.textContent =
      a === null ? "Wird abgefragt …" : "Die Server-Karte ist gerade nicht abrufbar. Alle Server gelten als unbekannt.";
    liste.replaceChildren();
  };

  let laeuft = false;
  let letzterAbruf = 0;
  let uhr: ReturnType<typeof setInterval> | null = null;
  const stoppe = (): void => {
    if (uhr !== null) clearInterval(uhr);
    uhr = null;
    document.removeEventListener("visibilitychange", tick);
  };
  const hol = (): void => {
    if (laeuft || uhr === null) return;
    laeuft = true;
    letzterAbruf = Date.now();
    knopf.disabled = true;
    void hole()
      .catch((): ServerAbruf => ({ art: "ausfall" }))
      .then((a) => {
        if (a.art === "weg") {
          stoppe(); // kein Betreiber oder Server-Netz aus: die Karte bleibt weg, nie wieder fragen
          abschnitt.dataset.zeigbar = "nein";
          liste.replaceChildren();
          return;
        }
        abschnitt.dataset.zeigbar = "ja";
        zeige(a);
      })
      .finally(() => {
        laeuft = false;
        knopf.disabled = false;
      });
  };
  function tick(): void {
    if (!abschnitt.isConnected) return stoppe();
    // Seitenwechsel und Tab-Wechsel lösen sonst Abruf auf Abruf aus.
    if (Date.now() - letzterAbruf < (k.mindestMs ?? SERVER_MINDEST_MS)) return;
    if (document.visibilityState === "visible" && sichtbar()) hol();
  }
  knopf.addEventListener("click", hol);
  uhr = setInterval(tick, k.takt ?? SERVER_ABRUF_MS);
  document.addEventListener("visibilitychange", tick);
  zeige(null);
  hol();
  return abschnitt;
}
